import {
  DASHBOARD_ACTIVE_CELL_ID,
  MEDIA_URL_PATTERNS,
} from '@modules/dashboard/constants';
import {
  getDashboardRow,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import {
  useIsDashboardEditable,
  useUpsertDashboard,
} from '@modules/dashboard/hooks';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { PermMediaRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import { DashboardCell } from 'generated/graphql';
import Lockr from 'lockr';
import { cloneDeep } from 'lodash';
import { useEffect, useState } from 'react';
import { DashboardImageUploader } from './DashboardImageUploader';

interface AddMediaButtonProps {
  rowID: string;
  cell: DashboardCell;
}

export function AddMediaButton({ rowID, cell }: AddMediaButtonProps) {
  const { upsertDashboard, loading: isSaveRequestLoading } =
    useUpsertDashboard();
  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const metadata = useDashboardStore(({ metadata }) => metadata);
  const [videoLink, setVideoLink] = useState('');
  const [shouldShowForm, setShouldShowForm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const activeCellID = Lockr.get(DASHBOARD_ACTIVE_CELL_ID);

  const isEditable = useIsDashboardEditable();

  useEffect(() => {
    if (activeCellID === cell.id) {
      setShouldShowForm(true);
    }
  }, [activeCellID, cell.id]);

  if (!dashboard) {
    return null;
  }

  const embedMediaLink = async ({
    videoWidgetUrl,
    imageWidgetUrl,
  }: {
    videoWidgetUrl?: string;
    imageWidgetUrl?: string;
  }) => {
    if (errorMessage) {
      return;
    }

    if (!isEditable || (!videoWidgetUrl && !imageWidgetUrl)) {
      return;
    }

    const clonedMetadata = cloneDeep(metadata);
    const clonedRow = getDashboardRow({
      rowID,
      rows: clonedMetadata.layout.rows,
    });

    if (!clonedRow) {
      return;
    }

    await upsertDashboard({
      dashboardProps: {
        metadataV2: clonedMetadata,
        updateWidget: {
          id: cell.contentID,
          imageWidget: imageWidgetUrl ? { url: imageWidgetUrl } : undefined,
          videoWidget: videoWidgetUrl ? { url: videoWidgetUrl } : undefined,
        },
      },
      shouldRefetchDashboard: true,
    });

    trackDashboardEvent({
      eventName: 'Dashboard Update Content',
      type: 'media',
      subtype: imageWidgetUrl ? 'image' : 'video',
      from: 'Dashboard Page',
    });

    setShouldShowForm(false);
  };

  const validVideoUrl = (url: string) => {
    const urlRegex = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
    if (!urlRegex.test(url)) {
      setErrorMessage('Please provide a valid URL.');
      return;
    }

    const isValidUrl = MEDIA_URL_PATTERNS.some(({ validateRegex }) =>
      validateRegex.test(url),
    );

    if (!isValidUrl) {
      setErrorMessage('Please provide a YouTube, Loom, or Descript link.');
      return;
    }

    setErrorMessage('');
  };

  if (!shouldShowForm) {
    return (
      <Stack
        sx={{
          width: '100%',
          height: '100%',
          cursor: 'pointer',
          backgroundColor: shouldShowForm
            ? (theme) => alpha(theme.palette.background.paper, 0.8)
            : undefined,
        }}
        justifyContent="center"
        alignItems="center"
        onClick={() => setShouldShowForm(true)}
      >
        <PermMediaRounded />
      </Stack>
    );
  }

  return (
    <Stack
      spacing={1}
      sx={{
        width: '100%',
        maxWidth: '350px',
        margin: '0px auto',
      }}
      alignItems="center"
      justifyContent="center"
    >
      <Box maxWidth="200px">
        <DashboardImageUploader
          id={cell.contentID}
          isDisabled={isSaveRequestLoading}
          onChange={() => {
            setVideoLink('');
          }}
          onCompleted={(url) => {
            embedMediaLink({
              imageWidgetUrl: url,
            });
          }}
        />
      </Box>
      <Stack direction="row" spacing={1} alignItems="center" width="100%">
        <Divider sx={{ flex: 1 }} />
        <Typography variant="subtitle1" color="text.secondary">
          or
        </Typography>
        <Divider sx={{ flex: 1 }} />
      </Stack>
      <Stack
        component="form"
        alignItems="center"
        width="100%"
        onSubmit={(eve) => {
          eve.preventDefault();
          embedMediaLink({
            videoWidgetUrl: videoLink,
          });
        }}
      >
        <TextField
          fullWidth
          autoFocus
          autoComplete="off"
          autoCapitalize="none"
          spellCheck="false"
          disabled={isSaveRequestLoading}
          variant="outlined"
          size="small"
          placeholder="Paste the video link..."
          helperText={
            !!errorMessage
              ? errorMessage
              : 'YouTube, Loom and Descript links are supported.'
          }
          error={!!errorMessage}
          onChange={(event) => {
            validVideoUrl(event.target.value);

            const link = MEDIA_URL_PATTERNS.reduce(
              (updatedLink, { extractRegex, transform }) => {
                return updatedLink.replace(extractRegex, (_, videoId) =>
                  transform(videoId),
                );
              },
              event.target.value,
            );

            setVideoLink(link);
          }}
          slotProps={{
            input: {
              endAdornment: (
                <LoadingButton
                  variant="contained"
                  size="small"
                  disabled={!videoLink || !!errorMessage}
                  type="submit"
                  loading={isSaveRequestLoading}
                >
                  Embed
                </LoadingButton>
              ),
            },
          }}
        />
      </Stack>
    </Stack>
  );
}
